import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText,
  Upload,
  Download,
  Eye,
  Edit2,
  Trash2,
  Search,
  Filter,
  Plus,
  File,
  FileType,
  Loader2,
  Calendar,
  User,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function PolicyPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Form state
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [filterCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/policies/list';
      if (filterCategory && filterCategory !== 'all') {
        url += `?category=${filterCategory}`;
      }
      
      const [policiesRes, categoriesRes] = await Promise.all([
        api.get(url),
        api.get('/policies/categories/list')
      ]);
      
      setPolicies(policiesRes.data.policies || []);
      setIsSuperAdmin(policiesRes.data.is_super_admin);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      toast.error('Only PDF and Word documents (.pdf, .doc, .docx) are allowed');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    if (!uploadForm.title.trim()) {
      toast.error('Please enter a policy title');
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      
      await api.post('/policies/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Policy uploaded successfully');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPolicy) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      
      await api.put(`/policies/${selectedPolicy.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Policy updated successfully');
      setShowEditDialog(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await api.delete(`/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleDownload = async (policy) => {
    try {
      const response = await api.get(`/policies/${policy.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', policy.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download policy');
    }
  };

  const handleView = async (policy) => {
    try {
      const response = await api.get(`/policies/${policy.id}/view`);
      setViewingPolicy(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load policy');
    }
  };

  const openEditDialog = (policy) => {
    setSelectedPolicy(policy);
    setUploadForm({
      title: policy.title,
      description: policy.description || '',
      category: policy.category
    });
    setSelectedFile(null);
    setShowEditDialog(true);
  };

  const resetUploadForm = () => {
    setUploadForm({ title: '', description: '', category: 'general' });
    setSelectedFile(null);
    setSelectedPolicy(null);
  };

  const filteredPolicies = policies.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') {
      return <FileText className="w-8 h-8 text-red-500" />;
    }
    return <FileType className="w-8 h-8 text-blue-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="policy-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Policy</h1>
          <p className="text-slate-600">
            {isSuperAdmin ? 'Manage and upload company policies' : 'View company policies'}
          </p>
        </div>
        {isSuperAdmin && (
          <Button 
            className="bg-[#2a9d8f] hover:bg-[#238b7e]"
            onClick={() => {
              resetUploadForm();
              setShowUploadDialog(true);
            }}
            data-testid="upload-policy-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Policy
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search policies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-policies-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-500" />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48" data-testid="category-filter-trigger">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Policies List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2a9d8f]" />
            Policies ({filteredPolicies.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPolicies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">No policies found</p>
              {isSuperAdmin && (
                <p className="text-sm mt-2">Click "Upload Policy" to add your first policy</p>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPolicies.map(policy => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow" data-testid={`policy-card-${policy.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {getFileIcon(policy.file_type)}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[#1e3a5f] truncate" title={policy.title}>
                          {policy.title}
                        </h4>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {getCategoryLabel(policy.category)}
                        </Badge>
                        {policy.description && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                            {policy.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(policy.created_at), 'MMM d, yyyy')}
                          <span>•</span>
                          <span>{formatFileSize(policy.file_size)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-1 mt-4 pt-3 border-t">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleView(policy)}
                        title="View"
                        data-testid={`view-policy-${policy.id}`}
                      >
                        <Eye className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDownload(policy)}
                        title="Download"
                        data-testid={`download-policy-${policy.id}`}
                      >
                        <Download className="w-4 h-4 text-green-500" />
                      </Button>
                      {isSuperAdmin && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(policy)}
                            title="Edit"
                            data-testid={`edit-policy-${policy.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(policy.id)}
                            title="Delete"
                            data-testid={`delete-policy-${policy.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg" data-testid="upload-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Upload className="w-5 h-5" />
              Upload Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Policy Title *</Label>
              <Input
                placeholder="Enter policy title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                data-testid="policy-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
              >
                <SelectTrigger data-testid="policy-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Brief description of the policy"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                rows={3}
                data-testid="policy-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Policy Document *</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  selectedFile ? 'border-green-500 bg-green-50' : 'border-slate-300 hover:border-[#2a9d8f]'
                }`}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-upload-area"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileSelect(e)}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="file-input"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <File className="w-6 h-6 text-green-600" />
                    <span className="font-medium text-green-700" data-testid="selected-file-name">{selectedFile.name}</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                      data-testid="clear-file-button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Click to select file</p>
                    <p className="text-xs text-slate-400 mt-1">PDF or Word documents only (max 10MB)</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} data-testid="cancel-upload-button">Cancel</Button>
            <Button onClick={handleUpload} disabled={saving} className="bg-[#2a9d8f]" data-testid="submit-upload-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Edit2 className="w-5 h-5" />
              Edit Policy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Policy Title *</Label>
              <Input
                placeholder="Enter policy title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Replace Document (Optional)</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  selectedFile ? 'border-green-500 bg-green-50' : 'border-slate-300 hover:border-[#2a9d8f]'
                }`}
                onClick={() => editFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={editFileInputRef}
                  onChange={(e) => handleFileSelect(e, true)}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <File className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-700">{selectedFile.name}</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-5 w-5"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Click to upload new document (leave empty to keep current)</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-[#1e3a5f]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit2 className="w-4 h-4 mr-2" />}
              Update Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Eye className="w-5 h-5" />
              {viewingPolicy?.title}
            </DialogTitle>
          </DialogHeader>

          {viewingPolicy && (
            <div className="mt-4">
              {viewingPolicy.file_type === 'pdf' ? (
                <iframe
                  src={`data:application/pdf;base64,${viewingPolicy.file_data}`}
                  className="w-full h-[70vh] border rounded"
                  title={viewingPolicy.title}
                />
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <FileType className="w-16 h-16 mx-auto text-blue-500 mb-4" />
                  <p className="text-slate-600 mb-4">
                    Word documents cannot be previewed in browser.
                  </p>
                  <Button onClick={() => handleDownload(viewingPolicy)}>
                    <Download className="w-4 h-4 mr-2" />
                    Download to View
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            {viewingPolicy && (
              <Button onClick={() => handleDownload(viewingPolicy)} className="bg-[#2a9d8f]">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
